import {
    defineComponent,
    computed,
    nextTick,
    onUpdated,
    ref,
    watch
} from "vue";

import ImageFallback            from "../../components/imageFallback.js";

import {
    createDiscordMarkdownParser,
    parseDiscordMarkdown
} from "../../utils/markdown.js";
import {
    fetchNextMessageContextPage,
    fetchPreviousMessageContextPage,
    loadMessageContext
} from "./message/messageContext.js";
import MessageLogContent from "./message/MessageLogContent.js";
import { LogsLoadMoreFooter } from "../../pages/logs/LogsLoadMoreFooter.js";
import { parseMessageLogs } from "./message/messageParser.js";

import {
    getMemberAvatar,
    getMemberDisplayName
} from "../../utils.js";

export default defineComponent({

    props: [ "log" ],

    setup(props) {
        const markdown          = createDiscordMarkdownParser();
        const refLoading        = ref(true);
        const refLoadingBefore  = ref(false);
        const refLoadingAfter   = ref(false);
        const refHasMoreBefore  = ref(true);
        const refHasMoreAfter   = ref(true);
        const refData           = ref(null);
        const refLogs           = ref([]);
        const refSelectedLog    = ref(null);
        const refLogPreview     = ref(null);
        const refLogList        = ref(null);
        const refSelectedNode   = ref(null);
        const refContextMode    = ref("channel");
        const refMembers        = ref(new Map());
        const refChannels       = ref(new Map());
        const messagePageSize   = 25;

        const viewModes = [
            { key: "channel", label: "Vue du salon", icon: "chat" },
            { key: "executor", label: "Vue de l'exécutant", icon: "person" },
            { key: "target", label: "Vue de la cible", icon: "person_search" },
        ];

        const isTargetModeDisabled = computed(() => !props.log.target);

        function isModeDisabled(mode) {
            if (mode === "executor")
                return !props.log.executor;

            if (mode === "target")
                return isTargetModeDisabled.value;

            return false;
        }

        function resolveContextMode(log, mode) {
            if (mode === "executor" && !log.executor)
                return "channel";

            if (mode === "target" && !log.target)
                return "channel";

            return mode;
        }

        function getContextFilter(log, mode) {
            const resolvedMode = resolveContextMode(log, mode);

            if (resolvedMode === "executor")
                return {
                    mode: resolvedMode,
                    channel: log.channel,
                    executor: log.executor,
                };

            if (resolvedMode === "target")
                return {
                    mode: resolvedMode,
                    channel: log.channel,
                    target: log.target,
                };

            return {
                mode: resolvedMode,
                channel: log.channel,
            };
        }

        function mergeEntities(context) {
            for (const member of context.members)
                if (member)
                    refMembers.value.set(member.id, member);

            for (const channel of context.channels)
                if (channel)
                    refChannels.value.set(channel.id, channel);
        }

        function getLogKey(log) {
            return `${log.type}:${log.createdAt}:${log.id ?? ""}`;
        }

        function applyLogs(context) {
            mergeEntities(context);

            refLogs.value = context.logs;
            refSelectedLog.value = context.selectedLog || props.log;
            refData.value = parseMessageLogs(refLogs.value, markdown, parseDiscordMarkdown);
        }

        function mergeLogs(context) {
            mergeEntities(context);

            const logs = new Map();

            for (const log of refLogs.value)
                logs.set(getLogKey(log), log);

            for (const log of context.logs)
                logs.set(getLogKey(log), log);

            refLogs.value = [ ...logs.values() ].sort((left, right) => left.createdAt - right.createdAt);
            refData.value = parseMessageLogs(refLogs.value, markdown, parseDiscordMarkdown);
        }

        let loadToken = 0;

        async function loadContext(log = props.log, mode = refContextMode.value) {
            const token = ++loadToken;
            const nextMode = resolveContextMode(log, mode);
            const filter = getContextFilter(log, nextMode);

            refContextMode.value = nextMode;
            refLoading.value = true;
            refHasMoreBefore.value = true;
            refHasMoreAfter.value = true;
            refSelectedNode.value = null;

            try {
                await loadMessageContext(
                    log,
                    filter.mode,
                    filter.executor,
                    filter.target,
                ).then((context) => {
                    if (token !== loadToken)
                        return;

                    applyLogs(context);
                });

            } catch (err) {
                if (token !== loadToken)
                    return;

                console.error(err);

            } finally {
                if (token !== loadToken)
                    return;

                refLoading.value = false;
            }
        }

        function setContextMode(mode) {
            if (isModeDisabled(mode))
                return;

            if (mode === refContextMode.value)
                return;

            refContextMode.value = mode;
            loadContext(props.log, mode);
        }

        watch(() => props.log, async (log) => {
            refLoadingAfter.value = false;
            refLoadingBefore.value = false;
            refHasMoreBefore.value = true;
            refHasMoreAfter.value = true;
            refSelectedNode.value = null;
            refLogs.value = [];
            refSelectedLog.value = null;
            refData.value = [];
            const nextMode = resolveContextMode(log, refContextMode.value);

            refContextMode.value = nextMode;

            await loadContext(log, nextMode);

        }, { immediate: true });

        async function loadPreviousLogs() {
            if (refLoadingBefore.value)
                return;

            refLoadingBefore.value = true;

            try {
                const filter = getContextFilter(props.log, refContextMode.value);
                const context = await fetchPreviousMessageContextPage(refLogs.value, filter);

                mergeLogs(context);
                if (context.logs.length < messagePageSize)
                    refHasMoreBefore.value = false;

            } finally {
                refLoadingBefore.value = false;
            }
        }

        async function loadNextLogs() {
            if (refLoadingAfter.value)
                return;

            refLoadingAfter.value = true;

            try {
                const filter = getContextFilter(props.log, refContextMode.value);
                const context = await fetchNextMessageContextPage(refLogs.value, filter);

                mergeLogs(context);
                if (context.logs.length < messagePageSize)
                    refHasMoreAfter.value = false;

            } finally {
                refLoadingAfter.value = false;
            }
        }

        function renderModeTabs() {
            return (
                <div class="log-message-context-toolbar-inner">
                    <div class="log-message-context-mode-tabs">
                        {viewModes.map((viewMode) => {
                            const isActive = refContextMode.value === viewMode.key;
                            const isDisabled = isModeDisabled(viewMode.key);

                            return (
                                <button
                                    type="button"
                                    class={{
                                        "log-message-context-mode-tab": true,
                                        "log-message-context-mode-tab-active": isActive,
                                        "log-message-context-mode-tab-disabled": isDisabled,
                                    }}
                                    disabled={isDisabled}
                                    onClick={() => setContextMode(viewMode.key)}
                                >
                                    <span class="log-message-context-mode-tab-label">
                                        <span class="material-symbols-outlined">{viewMode.icon}</span>
                                        {viewMode.label}
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    <button
                        type="button"
                        class="log-message-context-selected-jump"
                        onClick={scheduleSelectedLogScroll}
                    >
                        <span class="material-symbols-outlined">my_location</span>
                        Aller à la log sélectionnée
                    </button>
                </div>
            );
        }

        let hasChanged = false;

        watch(() => refData.value, () => {
            hasChanged = true;
        });

        let scrollFrame = 0;

        function getSelectedLogNode(container) {
            const selectedLog = refSelectedLog.value;

            if (!container || !selectedLog)
                return null;

            if (refSelectedNode.value?.isConnected)
                return refSelectedNode.value;

            refSelectedNode.value = null;

            const candidates = [ ...container.querySelectorAll(".log-message") ];
            const selectedId = selectedLog.id;
            const selectedMessageId = selectedLog.extra?.messageId;
            const selectedType = selectedLog.type;
            const selectedCreatedAt = String(selectedLog.createdAt);

            for (const candidate of candidates) {
                const candidateId = candidate.getAttribute("data-log-id");

                if (selectedId !== undefined && selectedId !== null && candidateId === String(selectedId))
                    return candidate;
            }

            if (selectedType?.startsWith("message_reaction") && selectedMessageId !== undefined && selectedMessageId !== null) {
                const targetMessageId = String(selectedMessageId);

                for (const candidate of candidates) {
                    const candidateType = candidate.getAttribute("data-log-type");
                    const candidateMessageId = candidate.getAttribute("data-log-message-id");

                    if (candidateType === selectedType && candidateMessageId === targetMessageId)
                        return candidate;
                }
            }

            for (const candidate of candidates) {
                const candidateType = candidate.getAttribute("data-log-type");
                const candidateCreatedAt = candidate.getAttribute("data-log-created-at");

                if (candidateType === selectedType && candidateCreatedAt === selectedCreatedAt)
                    return candidate;
            }

            for (const candidate of candidates) {
                if (candidate.getAttribute("data-log-created-at") === selectedCreatedAt)
                    return candidate;
            }

            return null;
        }

        function scrollSelectedLogIntoView() {
            const container = refLogList.value;
            const selected = getSelectedLogNode(container);

            if (!container || !selected)
                return;

            if (container.scrollHeight > container.clientHeight) {
                const targetTop = Math.max(
                    0,
                    Math.min(
                        selected.offsetTop - (container.clientHeight / 2),
                        container.scrollHeight - container.clientHeight,
                    ),
                );

                container.scrollTo({
                    top      : targetTop,
                    behavior : "smooth",
                });
            }

            if (typeof selected.scrollIntoView === "function") {
                selected.scrollIntoView({
                    behavior : "smooth",
                    block    : "center",
                    inline   : "nearest",
                });
            }
        }

        function scheduleSelectedLogScroll() {
            if (typeof window === "undefined")
                return;

            if (scrollFrame)
                cancelAnimationFrame(scrollFrame);

            scrollFrame = requestAnimationFrame(() => {
                scrollFrame = 0;
                scrollSelectedLogIntoView();
            });
        }

        onUpdated(() => {
            if (hasChanged) {
                hasChanged = false;
                nextTick(() => {
                    scheduleSelectedLogScroll();
                });
            }
        });

        function isSelectedMessageLog(log) {
            const selectedLog = refSelectedLog.value;

            if (!selectedLog)
                return false;

            if (selectedLog.type.startsWith("message_reaction")) {
                const selectedMessageId = selectedLog.extra?.messageId;
                const currentMessageId = log.extra?.messageId;
                const hasSelectedMessageId = selectedMessageId !== undefined && selectedMessageId !== null;
                const hasCurrentMessageId = currentMessageId !== undefined && currentMessageId !== null;

                if (hasSelectedMessageId && hasCurrentMessageId)
                    return String(selectedMessageId) === String(currentMessageId);
            }

            const selectedId = selectedLog.id;
            const currentId = log.id;

            if (selectedId !== undefined && selectedId !== null && currentId !== undefined && currentId !== null)
                return String(selectedId) === String(currentId);

            return log.createdAt === selectedLog.createdAt;
        }

        function getReactionKind(log) {
            if (log.type === "message_reaction_add")
                return "add";

            return "remove";
        }

        function isDiscordEmojiId(value) {
            return typeof value === "string" && /^\d{16,22}$/.test(value);
        }

        function getReactionImageId(extra) {
            if (isDiscordEmojiId(extra?.reactionId))
                return extra.reactionId;

            if (isDiscordEmojiId(extra?.reaction))
                return extra.reaction;

            return null;
        }

        function getReactionCount(extra) {
            const count = extra?.count;

            if (typeof count === "number")
                return count;

            const normal = extra?.countDetails?.normal;
            const burst = extra?.countDetails?.burst;

            if (typeof normal === "number" || typeof burst === "number")
                return (normal || 0) + (burst || 0);

            return null;
        }

        function renderReactionEmoji(extra) {
            const reactionId = getReactionImageId(extra);
            const reactionName = extra?.reactionName || extra?.reaction;

            if (reactionId) {
                return (
                    <ImageFallback
                        class="log-message-selected-reaction-emoji"
                        src={`https://cdn.discordapp.com/emojis/${reactionId}?size=240&animated=true`}
                        default="https://cdn.discordapp.com/embed/avatars/0.png"
                        alt={reactionName || reactionId}
                    />
                );
            }

            return (
                <span class="log-message-selected-reaction-unicode">
                    {reactionName || extra?.reactionKey || "?"}
                </span>
            );
        }

        function renderSelectedReaction(log) {
            const selectedLog = refSelectedLog.value;

            if (!selectedLog?.type?.startsWith("message_reaction"))
                return null;

            if (selectedLog.extra?.messageId !== log.extra?.messageId)
                return null;

            const reactionName = selectedLog.extra?.reactionName || selectedLog.extra?.reaction || selectedLog.extra?.reactionKey || "Réaction inconnue";
            const reactionCount = getReactionCount(selectedLog.extra);

            return (
                <div class={`log-message-selected-reaction log-message-selected-reaction-${getReactionKind(selectedLog)}`}>
                    <span class="material-symbols-outlined">
                        {getReactionKind(selectedLog) === "add" ? "add_reaction" : "heart_minus"}
                    </span>
                    {renderReactionEmoji(selectedLog.extra)}
                    <div>
                        <b>{reactionCount === null ? "—" : reactionCount}</b>
                        <span>{reactionName}</span>
                    </div>
                </div>
            );
        }

        function renderLogMessage(log) {
            const executor = refMembers.value.get(log.executor);
            const authorName = executor ? getMemberDisplayName(executor) : "Utilisateur inconnu";
            const authorAvatar = executor ? getMemberAvatar(executor) : undefined;
            const authorPath = executor?.id ? `/users/${executor.id}` : null;
            const isSelected = isSelectedMessageLog(log);
            const dataLogId = log.id === undefined || log.id === null ? undefined : String(log.id);
            const dataMessageId = log.extra?.messageId === undefined || log.extra?.messageId === null ? undefined : String(log.extra.messageId);

            function getClassNames() {
                return {
                    "log-message"   : true,
                    "edited"        : (log.type === "message_edit"),
                    "deleted"       : (log.type === "message_delete"),
                    "selected"      : isSelected,
                };
            }

            return (
                <div
                    class={getClassNames()}
                    data-log-type={log.type}
                    data-log-created-at={String(log.createdAt)}
                    data-log-id={dataLogId}
                    data-log-message-id={dataMessageId}
                    ref={isSelected
                        ? (node) => {
                            refSelectedNode.value = node;
                        }
                        : undefined}
                >
                    {authorPath
                        ? (
                            <router-link to={authorPath} class="log-message-author-avatar">
                                <ImageFallback src={authorAvatar} default="https://cdn.discordapp.com/embed/avatars/0.png" alt={authorName} />
                            </router-link>
                        )
                        : (
                            <span class="log-message-author-avatar">
                                <ImageFallback src={authorAvatar} default="https://cdn.discordapp.com/embed/avatars/0.png" alt={authorName} />
                            </span>
                        )}

                    {authorPath
                        ? <router-link to={authorPath} class="log-message-author-name">{authorName}</router-link>
                        : <span class="log-message-author-name">{authorName}</span>}

                    <MessageLogContent
                        log={log}
                        onImageSettled={() => {
                            if (isSelectedMessageLog(log))
                                scheduleSelectedLogScroll();
                        }}
                    />

                    {renderSelectedReaction(log)}
                </div>
            );
        }

        return () => {
            if (refLoading.value)
                return (
                    <div class="log-preview log-message-context-preview">
                        <div class="log-message-context-toolbar">
                            {renderModeTabs()}
                        </div>

                        <div class="log-message-context-scroll" />

                        <div class="log-message-context-actions">
                            <LogsLoadMoreFooter
                                loading={true}
                                hasMore={true}
                                label="Chargement en cours…"
                                loadingLabel="Chargement en cours…"
                                onloadMore={() => {}}
                            />
                        </div>
                    </div>
                );

            if (props.log.type.startsWith("message_"))
                return (
                    <div class="log-preview log-message-context-preview" ref={refLogPreview}>
                        <div class="log-message-context-toolbar">
                            {renderModeTabs()}
                        </div>

                        <div class="log-message-context-scroll" ref={refLogList}>
                            {refData.value.map(renderLogMessage)}
                        </div>

                        <div class="log-message-context-actions">
                            <LogsLoadMoreFooter
                                loading={refLoadingBefore.value}
                                hasMore={refHasMoreBefore.value}
                                icon="expand_less"
                                label="Charger 25 logs précédents"
                                loadingLabel="Chargement…"
                                onloadMore={loadPreviousLogs}
                                class="log-message-context-load-before"
                            />

                            <LogsLoadMoreFooter
                                loading={refLoadingAfter.value}
                                hasMore={refHasMoreAfter.value}
                                icon="expand_more"
                                label="Charger 25 logs suivants"
                                loadingLabel="Chargement…"
                                onloadMore={loadNextLogs}
                                class="log-message-context-load-after"
                            />
                        </div>
                    </div>
                );

            return (
                <div class="log-preview">
                    <div class="log-entry">
                        Log pas encore supportée.
                    </div>
                </div>
            );
        };
    },
});

// import { onMounted, ref }   from "vue";
// import { useRoute }         from "vue-router";
// import { useLocale }        from "../../locales/i18n.js";
// import ImageFallback        from "../../components/imageFallback.js";

// import {
//     getMemberAvatar,
//     getMemberDisplayName,
//     useFetch,
//     Diff,
// } from "../../utils.js";

// export default {

//     setup() {
//         const { i18n }      = useLocale();
//         const { fetch }     = useFetch();
//         const route         = useRoute();
//         const refLoading    = ref(true);
//         const refLog        = ref(null);

//         onMounted(async () => {
//             const createdAt = Number(route.params["id"]);

//             const { result } = await fetch(`/api/logs?before=${createdAt + 1}&limit=10`, {
//                 headers: {
//                     "content-type": "application/json",
//                 },
//                 body    : JSON.stringify({}),
//                 method  : "POST",
//             })
//                 .then((res) => res.json());

//             if (result) {
//                 const log = result.find((log) => log.createdAt === createdAt);

//                 if (log) {
//                     if (log.type === "voice_move") {
//                         const { result, members } = await fetch(`/api/logs`, {
//                             headers: {
//                                 "content-type": "application/json",
//                             },
//                             body: JSON.stringify({
//                                 types: [ "voice_join", "voice_move", "voice_leave" ],
//                             }),
//                             method: "POST",
//                         })
//                             .then((res) => res.json());

//                         console.log(result, members);

//                     } else {
//                         const { result, members } = await fetch(`/api/logs`, {
//                             headers: {
//                                 "content-type": "application/json",
//                             },
//                             body: JSON.stringify({
//                                 types       : [ "message_create", "message_edit", "message_delete" ],
//                                 properties  : { "messageId": log.extra.messageId },
//                             }),
//                             method: "POST",
//                         })
//                             .then((res) => res.json());

//                         for (let i = 0; i < result.length; i++) {
//                             const relatedLogA = result[i];
//                             const relatedLogB = result[i + 1];

//                             if (relatedLogA.extra.content && relatedLogB)
//                                 relatedLogA.differences = Diff.diffChars(relatedLogB.extra.content, relatedLogA.extra.content,);

//                             relatedLogA.executor = members.find((member) => (member.id === relatedLogA.executor));
//                         }

//                         refLog.value = {
//                             targetLog   : log,
//                             relatedLogs : result,
//                         };
//                     }
//                 }
//             }

//             refLoading.value = false;
//         });

//         function renderAttrs(relatedLog, targetLog) {
//             return relatedLog.id === targetLog.id
//                 ? { class: "log-selected-target" }
//                 : { class: "log-outdated" };
//         }

//         function renderRelatedAndLog(relatedLog, targetLog) {
//             const attrs = renderAttrs(relatedLog, targetLog);

//             function renderMessageLogContent(log) {
//                 if (log.differences) {
//                     return log.differences.map((difference) => {
//                         return difference.added
//                             ? <span class="text-diff-added">{difference.value}</span>
//                             : difference.removed
//                                 ? <strike class="text-diff-removed">{difference.value}</strike>
//                                 : difference.value;
//                     });
//                 }

//                 return log.extra.content;
//             }

//             switch (relatedLog.type) {
//                 case "message_delete":
//                     return (
//                         <div {...attrs}>
//                             <div class="log-show-action">
//                                 {i18n("panel.showLogs.messageAction.deleted")}
//                             </div>
//                         </div>
//                     );

//                 case "message_edit":
//                     return (
//                         <div {...attrs}>
//                             <div class="log-message">
//                                 <router-link to={`/users/${relatedLog.executor.id}`}  class="log-message-author-avatar">
//                                     <ImageFallback src={getMemberAvatar(relatedLog.executor)} default="https://cdn.discordapp.com/embed/avatars/0.png" alt={getMemberDisplayName(relatedLog.executor)} />
//                                 </router-link>

//                                 <router-link to={`/users/${relatedLog.executor.id}`} class="log-message-author-name">
//                                     {getMemberDisplayName(relatedLog.executor)}
//                                 </router-link>

//                                 <div class="log-message-content">
//                                     {renderMessageLogContent(relatedLog)}
//                                 </div>
//                             </div>

//                             <div class="log-show-action">
//                                 {i18n("panel.showLogs.messageAction.edited")}
//                             </div>
//                         </div>
//                     );

//                 case "message_create":
//                     return (
//                         <div {...attrs}>
//                             <div class="log-message">
//                                 <router-link to={`/users/${relatedLog.executor.id}`}  class="log-message-author-avatar">
//                                     <ImageFallback src={getMemberAvatar(relatedLog.executor)} default="https://cdn.discordapp.com/embed/avatars/0.png" alt={getMemberDisplayName(relatedLog.executor)} />
//                                 </router-link>

//                                 <router-link to={`/users/${relatedLog.executor.id}`} class="log-message-author-name">
//                                     {getMemberDisplayName(relatedLog.executor)}
//                                 </router-link>

//                                 <div class="log-message-content">
//                                     {renderMessageLogContent(relatedLog)}
//                                 </div>
//                             </div>

//                             <div class="log-show-action">
//                                 {i18n("panel.showLogs.messageAction.created")}
//                             </div>
//                         </div>
//                     );

//                 default:
//                     throw new Error("Render not implemented.");
//             }
//         }

//         return () => {
//             if (refLoading.value)
//                 return <div class="notice">{i18n("panel.showLogs.loading_text")}</div>;

//             if (!refLog.value)
//                 return <div class="notice">{i18n("panel.showLogs.log_not_found")}</div>;

//             const { targetLog, relatedLogs } = refLog.value;

//             return (
//                 <div id="app">
//                     {relatedLogs.map((relatedLog) => renderRelatedAndLog(relatedLog, targetLog))}
//                 </div>
//             );
//         };
//     }
// }
